import { SOCIAL_FIELDS, socialPayload } from '../../../lib/utils/clientSocial';
/**
 * Stato iniziale e mapping di submission del drawer "Nuova anagrafica".
 * La shape di formData e della submission è IDENTICA a quella di
 * ClientFormWizard: i chiamanti (createClient/updateClient nei punti di
 * ingresso) non distinguono da dove arriva il salvataggio.
 */
import { normalizeWebsiteUrl } from '../../../lib/utils/websiteUtils';

export function buildInitialFormData(initialData?: any): Record<string, any> {
  return {
    entity_type: initialData?.entity_type || 'company',
    client_code: initialData?.client_code || '',
    business_name: initialData?.business_name || '',
    trade_name: initialData?.trade_name || '',
    tax_code: initialData?.tax_code || initialData?.fiscal_code || '',
    vat_number: initialData?.vat_number || '',
    sdi_code: initialData?.sdi_code || '',
    pec: initialData?.pec || '',
    email_amministrazione: initialData?.email_amministrazione || '',
    ateco_code: initialData?.ateco_code || '',
    website: initialData?.website || '',
    ...Object.fromEntries(SOCIAL_FIELDS.map(key => [key, initialData?.[key] || ''])),
    birth_date: initialData?.birth_date || '',

    // Indirizzo sede legale
    country: initialData?.country || 'Italia',
    address: initialData?.street_address || initialData?.address || '',
    street_number: initialData?.street_number || '',
    city: initialData?.city || '',
    province: initialData?.province || '',
    postal_code: initialData?.postal_code || '',
    region: initialData?.region || '',

    // Sede operativa legacy + sedi multiple
    has_operational_address: initialData?.has_operational_address || false,
    operational_country: initialData?.operational_country || '',
    operational_address: initialData?.operational_address || '',
    operational_city: initialData?.operational_city || '',
    operational_province: initialData?.operational_province || '',
    operational_postal_code: initialData?.operational_postal_code || '',
    operational_region: initialData?.operational_region || '',
    operational_locations: initialData?.operational_locations || [],

    // Recapiti
    email: initialData?.email || '',
    phone: initialData?.phone || '',
    mobile: initialData?.mobile || initialData?.whatsapp_number || '',
    country_code: initialData?.country_code || '+39',

    // Marketing e consensi
    marketing_consent_email: initialData?.marketing_consent_email || false,
    marketing_consent_sms: initialData?.marketing_consent_sms || false,
    marketing_consent_phone: initialData?.marketing_consent_phone || false,
    newsletter_consent: initialData?.newsletter_consent || initialData?.newsletter_subscribed || false,
    // Indirizzo a cui arriva la newsletter: vuoto = email principale (vedi lib/newsletter/newsletterRecipient.ts)
    newsletter_email: initialData?.newsletter_email || '',
    // In modifica si salva solo se il record l'ha davvero portato (altrimenti si azzererebbe)
    newsletter_email_loaded: !initialData?.id || 'newsletter_email' in initialData,
    marketing_notes: initialData?.marketing_notes || '',
    marketing_consent_date: initialData?.marketing_consent_date || '',

    // Referenti (contacts_loaded=true solo quando arrivano dal client in modifica:
    // senza questo flag il submit in edit NON invia contacts, per non rischiare
    // il diff soft-unlink di updateClient su una lista mai caricata)
    contacts: initialData?.contacts || [],
    contacts_loaded: initialData?.contacts_loaded || false,

    // Classificazione
    interest_categories: initialData?.interest_categories || [],
    interest_notes: initialData?.interest_notes || '',
    selected_tags: initialData?.selected_tags || [],
    selected_settori: initialData?.selected_settori || [],

    // Assegnazione / stato / fonte
    assigned_to: initialData?.assigned_to || '',
    sales_rep_id: initialData?.sales_rep_id || '',
    referrer_id: initialData?.referrer_id || '',
    partner_id: initialData?.partner_id || '',
    lead_source: initialData?.lead_source || '',
    // Dettaglio fonte (es. "Chat WhatsApp arrivata sul numero ..."): passa solo se il
    // chiamante lo manda, cosi' la modifica di un'anagrafica non lo tocca.
    ...(initialData?.lead_source_details ? { lead_source_details: initialData.lead_source_details } : {}),
    stato_cliente: initialData?.stato_cliente || '',
    pipeline_id: initialData?.pipeline_id || null,
    pipeline_stage_id: initialData?.pipeline_stage_id || null,
    lead_stage: initialData?.lead_stage || null,

    // Pagamenti e listini
    default_payment_method_id: initialData?.default_payment_method_id || '',
    default_price_list_id: initialData?.default_price_list_id || '',
    // Aliquota IVA abituale del cliente ('' = nessuna), applicabile alle offerte
    default_vat_rate: initialData?.default_vat_rate != null ? String(Number(initialData.default_vat_rate)) : '',
    // Codice IVA (esenzione) del cliente ('' = nessuno), solo org con la funzione attiva
    vat_code_id: initialData?.vat_code_id || '',
  };
}

/** Stesso mapping di ClientFormWizard.handleComplete (street_address, whatsapp_number, contatti). */
export function buildSubmissionData(formData: Record<string, any>): Record<string, any> {
  const { contacts_loaded, newsletter_email_loaded, newsletter_email, ...data } = formData;
  return {
    ...data,
    ...(newsletter_email_loaded !== false ? { newsletter_email: (newsletter_email || '').trim() || null } : {}),
    ...socialPayload(data),
    street_address: formData.address,
    whatsapp_number: formData.mobile,
    newsletter_subscribed: formData.newsletter_consent,
    website: formData.website ? normalizeWebsiteUrl(formData.website) : '',
    default_vat_rate: formData.default_vat_rate === '' || formData.default_vat_rate == null ? null : Number(formData.default_vat_rate),
    vat_code_id: formData.vat_code_id || null,
    selected_interest_categories: formData.interest_categories,
    selected_settori: formData.selected_settori || [],
    contacts: (formData.contacts || []).map((contact: any) => ({
      ...contact,
      name:
        `${(contact.first_name || '').trim()} ${(contact.last_name || '').trim()}`.trim() ||
        'Contatto senza nome',
      newsletter_subscribed: contact.newsletter_subscribed || false,
    })),
  };
}

/** Divide il `name` unico dei contatti persistiti nei campi nome/cognome del form. */
export function splitContactName(name?: string | null): { first_name: string; last_name: string } {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  return { first_name: parts[0] || '', last_name: parts.slice(1).join(' ') };
}

/**
 * Mapping del selectedClient arricchito (tags/settori/interests come oggetti)
 * nella shape `initialData` attesa da buildInitialFormData, per la modalità
 * MODIFICA del drawer. I referenti passano SOLO se il client li ha caricati
 * (lista merged legacy+first-class di fetchClientById/fetchLeads): con
 * contacts_loaded=false il drawer non li mostra né li invia al salvataggio,
 * così il diff soft-unlink di updateClient non tocca link mai visti.
 */
export function clientToDrawerInitialData(client: any): Record<string, any> {
  if (!client) return {};
  const contactsLoaded = Array.isArray(client.contacts);
  return {
    ...client,
    selected_tags: (client.tags || []).map((t: any) => t.id).filter(Boolean),
    selected_settori: (client.settori || [])
      .map((s: any) => s.settore_id || s.settore?.id)
      .filter(Boolean),
    interest_categories:
      client.interest_categories ||
      (client.interests || [])
        .map((i: any) => i.interest_category_id || i.interest_category?.id)
        .filter(Boolean),
    contacts: contactsLoaded
      ? client.contacts
          .filter((c: any) => c.is_active !== false)
          .map((c: any) => ({ ...c, ...splitContactName(c.name) }))
      : [],
    contacts_loaded: contactsLoaded,
  };
}

/**
 * Campi mantenuti da "Crea e aggiungine un'altra": il contesto di lavoro
 * (paese, stato, pipeline, fonte, assegnazione) resta, i dati anagrafici no.
 */
export const KEEP_ON_CREATE_ANOTHER = [
  'entity_type',
  'country',
  'country_code',
  'stato_cliente',
  'pipeline_id',
  'pipeline_stage_id',
  'lead_stage',
  'lead_source',
  'assigned_to',
  'sales_rep_id',
  'referrer_id',
] as const;
